<template>
  <div class="w-full max-w-2xl mx-auto bg-white rounded-2xl p-8">
    <!-- Заголовок -->
    <div class="text-center mb-8">
      <div
        class="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
        :class="result.isPassed ? 'bg-green-100' : 'bg-red-100'"
      >
        <svg
          v-if="result.isPassed"
          class="w-10 h-10 text-green-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <svg
          v-else
          class="w-10 h-10 text-red-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </div>

      <div
        class="text-2xl font-bold mb-2"
        :class="result.isPassed ? 'text-green-900' : 'text-red-900'"
      >
        {{ result.isPassed ? 'Тест пройден!' : 'Тест не пройден' }}
      </div>

      <p class="text-gray-600">
        {{
          result.isPassed
            ? 'Поздравляем! Вы успешно прошли тестирование.'
            : getFailureMessage()
        }}
      </p>
    </div>

    <!-- Статистика -->
    <div class="grid grid-cols-2 gap-6 mb-8">
      <div class="text-center p-6 bg-gray-50 rounded-xl">
        <div class="text-3xl font-bold text-gray-900 mb-2">
          {{ result.correctAnswers }}
        </div>
        <div class="text-sm text-gray-600">Правильных ответов</div>
      </div>

      <div class="text-center p-6 bg-gray-50 rounded-xl">
        <div class="text-3xl font-bold text-gray-900 mb-2">
          {{ result.totalQuestions }}
        </div>
        <div class="text-sm text-gray-600">Всего вопросов</div>
      </div>
    </div>

    <!-- Прогресс -->
    <div class="mb-8">
      <div class="flex justify-between text-sm text-gray-600 mb-2">
        <span>Результат</span>
        <span
          >{{
            Math.round((result.correctAnswers / result.totalQuestions) * 100)
          }}%</span
        >
      </div>
      <div class="w-full bg-gray-200 rounded-full h-3">
        <div
          class="h-3 rounded-full transition-all duration-500"
          :class="result.isPassed ? 'bg-green-500' : 'bg-red-500'"
          :style="{
            width: `${(result.correctAnswers / result.totalQuestions) * 100}%`,
          }"
        ></div>
      </div>
    </div>

    <!-- Дополнительная информация -->
    <div v-if="!result.isPassed" class="space-y-4 mb-6">
      <!-- Время истекло -->
      <div
        v-if="result.failureReason === 0"
        class="bg-yellow-50 border border-yellow-200 rounded-xl p-4"
      >
        <div class="flex items-center gap-3">
          <svg
            class="w-5 h-5 text-yellow-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <div class="font-medium text-yellow-800">Время истекло</div>
            <div class="text-sm text-yellow-700">
              Тест был завершен автоматически по истечении времени.
            </div>
          </div>
        </div>
      </div>

      <!-- Критичные вопросы -->
      <div
        v-if="result.failureReason === 1"
        class="bg-red-50 border border-red-200 rounded-xl p-4"
      >
        <div class="flex items-center gap-3">
          <svg
            class="w-5 h-5 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
          <div>
            <div class="font-medium text-red-800">
              Неправильный ответ на критичный вопрос
            </div>
            <div class="text-sm text-red-700">
              Тест завершен из-за неправильного ответа на критичный вопрос.
            </div>
          </div>
        </div>
      </div>

      <!-- Недостаточное количество правильных ответов -->
      <div
        v-if="result.failureReason === 2"
        class="bg-orange-50 border border-orange-200 rounded-xl p-4"
      >
        <div class="flex items-center gap-3">
          <svg
            class="w-5 h-5 text-orange-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          <div>
            <div class="font-medium text-orange-800">
              Недостаточно правильных ответов
            </div>
            <div class="text-sm text-orange-700">
              Не набрано минимальное количество правильных ответов для
              прохождения теста.
            </div>
          </div>
        </div>
      </div>
      <!-- Недостаточное количество правильных ответов -->
      <div
        v-if="result.failureReason === 3"
        class="bg-orange-50 border border-orange-200 rounded-xl p-4"
      >
        <div class="flex items-center gap-3">
          <svg
            class="w-5 h-5 text-orange-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          <div>
            <div class="font-medium text-orange-800">
              Попытка завершена пользователем
            </div>
            <div class="text-sm text-orange-700">
              Пользователь прервал прохождение теста.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Рекомендации -->
    <div
      v-if="!result.isPassed"
      class="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6"
    >
      <div class="flex items-start gap-3">
        <svg
          class="w-5 h-5 text-blue-600 mt-0.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <div>
          <div class="font-medium text-blue-800 mb-1">Рекомендации</div>
          <div class="text-sm text-blue-700">
            Рекомендуем повторить материал курса и попробовать пройти тест еще
            раз. Обратите внимание на темы, в которых были допущены ошибки.
          </div>
        </div>
      </div>
    </div>

    <!-- Детали ответов -->
    <div class="mb-6">
      <button
        @click="showDetails = !showDetails"
        class="w-full py-2 border border-blue-200 text-blue-600 rounded-xl hover:bg-blue-50 transition-colors"
      >
        {{ showDetails ? 'Скрыть детали ответов' : 'Показать детали ответов' }}
      </button>
    </div>

    <div v-if="showDetails" class="mb-8">
      <div v-if="loadingDetails" class="text-center text-gray-500">Загрузка...</div>
      <div v-else-if="attemptDetails?.questionResults?.length > 0" class="space-y-4">
        <div
          v-for="(resultItem, index) in attemptDetails.questionResults"
          :key="resultItem.questionId"
          class="border border-gray-200 rounded-lg p-4"
        >
          <div class="flex items-start justify-between mb-3">
            <div class="flex-1">
              <div class="inline-block w-6 h-6 bg-primary text-white text-xs rounded-full text-center leading-6 mr-3 mb-1">
                {{ index + 1 }}
              </div>
              <div class="font-medium text-gray-900 inline" v-html="resultItem.question"></div>
            </div>
            <div class="flex items-center gap-2 ml-4">
              <span :class="[
                'px-2 py-1 text-xs rounded',
                resultItem.isCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              ]">
                {{ resultItem.isCorrect ? 'Верно' : (resultItem.userAnswers?.length > 0 ? 'Неверно' : 'Не отвечен') }}
              </span>
              <span v-if="resultItem.isCritical" class="px-2 py-1 text-xs rounded bg-orange-100 text-orange-800">
                Критический
              </span>
            </div>
          </div>

          <div class="text-sm space-y-2">
            <div>
              <span class="font-medium text-gray-700">Ответ пользователя:</span>
              <span class="ml-2 text-gray-900">{{ resultItem.userAnswers?.length > 0 ? resultItem.userAnswers.join(', ') : 'Не отвечен' }}</span>
            </div>
            <div>
              <span class="font-medium text-gray-700">Правильные ответы:</span>
              <span class="ml-2 text-green-600">{{ resultItem.correctAnswers?.join(', ') }}</span>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-center text-gray-500">Нет данных для отображения</div>
    </div>

    <!-- Кнопки действий -->
    <div class="flex gap-4 justify-end">
      <button
        @click="$emit('retryTest')"
        class="px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
      >
        Пройти еще раз
      </button>
      <button
        @click="$emit('close')"
        class="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
      >
        Закрыть
      </button>

      <!--      <button-->
      <!--        v-if="!result.isPassed"-->
      <!--        @click="$emit('retryTest')"-->
      <!--        class="px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors">-->
      <!--        Пройти еще раз-->
      <!--      </button>-->

      <button
        v-if="result.isPassed"
        @click="$emit('close')"
        class="px-6 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors"
      >
        Продолжить обучение
      </button>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, onMounted } from 'vue'
import apiService from '@/services/api'

const props = defineProps({
  result: {
    type: Object,
    required: true,
  },
  courseId: {
    type: String,
    required: false,
  }
})

defineEmits(['close', 'retryTest'])

const getFailureMessage = () => {
  const reasonMap = [
    'Время вышло',
    'Неверный ответ на критический вопрос',
    'Недостаточно правильных ответов',
    'Попытка прервана пользователем',
  ]

  return reasonMap[props.result.failureReason] // Возвращает текст или исходный ключ, если совпадения нет
}

const attemptDetails = ref(null)
const loadingDetails = ref(false)
const showDetails = ref(false)

onMounted(async () => {
  try {
    const cid = props.courseId || props.result?.courseId
    if (Array.isArray(props.result?.questionResults) && props.result.questionResults.length > 0) {
      attemptDetails.value = { questionResults: props.result.questionResults }
      return
    }
    if (cid && props.result?.attemptId && props.result?.testId) {
      loadingDetails.value = true
      attemptDetails.value = await apiService.getAttemptDetails(
        cid,
        props.result.testId,
        props.result.attemptId
      )
    }
  } catch (e) {
    console.error('Ошибка при загрузке деталей попытки:', e)
  } finally {
    loadingDetails.value = false
  }
})
</script>

<style scoped>
/* Мобильная адаптивность */
@media (max-width: 768px) {
  /* Основной контейнер */
  .w-full.max-w-2xl {
    max-width: 100%;
    width: 100%;
    margin: 0;
    height: 100vh;
    border-radius: 0;
  }

  /* Header */
  .py-6 {
    padding: 16px;
  }

  .text-2xl {
    font-size: 1.5rem;
    line-height: 1.3;
  }

  .w-10.h-10 {
    width: 36px;
    height: 36px;
  }

  /* Основное содержимое */
  .py-6:last-child {
    padding: 16px;
    overflow-y: auto;
    max-height: calc(100vh - 120px);
  }

  /* Результат теста */
  .text-center.mb-6 {
    margin-bottom: 20px;
  }

  .w-20.h-20 {
    width: 64px;
    height: 64px;
  }

  .text-xl {
    font-size: 1.125rem;
  }

  .text-lg {
    font-size: 1rem;
  }

  /* Детали результатов */
  .grid.grid-cols-2.gap-6 {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .text-center.p-4 {
    padding: 16px;
    border-radius: 12px;
  }

  .text-2xl.font-bold {
    font-size: 1.5rem;
  }

  .text-sm {
    font-size: 0.875rem;
  }

  /* Рекомендации */
  .rounded-xl.p-4 {
    padding: 16px;
    border-radius: 12px;
  }

  .mb-6 {
    margin-bottom: 20px;
  }

  /* Кнопки действий */
  .flex.gap-4 {
    flex-direction: column;
    gap: 12px;
  }

  .px-6.py-3 {
    padding: 14px 16px;
    font-size: 0.875rem;
    min-height: 48px;
    border-radius: 12px;
  }
}

@media (max-width: 480px) {
  .py-6 {
    padding: 12px;
  }

  .text-2xl {
    font-size: 1.25rem;
  }

  .w-20.h-20 {
    width: 56px;
    height: 56px;
  }

  .text-xl {
    font-size: 1rem;
  }

  .text-lg {
    font-size: 0.9rem;
  }

  .text-center.p-4 {
    padding: 12px;
  }

  .text-2xl.font-bold {
    font-size: 1.25rem;
  }

  .rounded-xl.p-4 {
    padding: 12px;
  }

  .px-6.py-3 {
    padding: 12px 16px;
    font-size: 0.8rem;
    min-height: 44px;
  }
}
</style>
